import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check, ChevronsUpDown } from "lucide-react";
import { removeQueriesForRestaurant } from "@/rc/tenant";
import { useRestaurantStore } from "@/stores/restaurantStore";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { getDefaultLandingForRole } from "@/lib/auth/routePermissions";
import { cn } from "@/lib/utils";
import { getInitials } from "@/utils/displayInitial";

interface RestaurantSwitcherProps {
  className?: string;
}

const roleLabelMap: Record<string, string> = {
  admin: "Admin",
  staff: "Staff",
  kitchen: "Kitchen",
};

export function RestaurantSwitcher({ className }: RestaurantSwitcherProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const memberships = useRestaurantStore((s) => s.memberships);
  const activeRestaurantId = useRestaurantStore((s) => s.activeRestaurantId);
  const setRestaurantState = useRestaurantStore((s) => s.setRestaurantState);

  const sortedMemberships = useMemo(
    () =>
      [...memberships].sort((a, b) =>
        (a.restaurantName ?? "").localeCompare(b.restaurantName ?? "")
      ),
    [memberships]
  );

  const activeMembership = sortedMemberships.find(
    (m) => m.restaurantId === activeRestaurantId
  );

  if (sortedMemberships.length <= 1 || !activeRestaurantId) return null;

  const activeName =
    activeMembership?.restaurantName ??
    activeMembership?.slug ??
    "Select restaurant";

  function handleSwitch(nextId: string) {
    if (nextId === activeRestaurantId) {
      setOpen(false);
      return;
    }
    const match = sortedMemberships.find((m) => m.restaurantId === nextId);
    if (!match) return;
    const previousRestaurantId = activeRestaurantId;
    setRestaurantState({
      activeRestaurantId: match.restaurantId,
      activeRole: match.role,
      activeSlug: match.slug,
      activeMembershipId: match.id,
      memberships: sortedMemberships,
    });
    removeQueriesForRestaurant(queryClient, previousRestaurantId || "");
    setOpen(false);
    navigate(getDefaultLandingForRole(match.role), { replace: true });
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          role="combobox"
          aria-expanded={open}
          aria-label="Switch restaurant"
          className={cn(
            "flex w-full items-center gap-2.5 rounded-md border border-sidebar-border bg-sidebar-accent/50 px-2.5 py-2 text-left text-sm transition-colors",
            "hover:bg-sidebar-accent focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-sidebar-ring",
            className
          )}
        >
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-primary/15 text-[10px] font-bold text-primary">
            {getInitials(activeName)}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-xs font-semibold text-sidebar-foreground">
              {activeName}
            </p>
            {activeMembership?.role && (
              <p className="text-[10px] text-sidebar-foreground/60 capitalize">
                {roleLabelMap[activeMembership.role] ?? activeMembership.role}
              </p>
            )}
          </div>
          <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/40" />
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] p-1"
        align="start"
        sideOffset={6}
      >
        <div className="max-h-64 overflow-y-auto space-y-1">
          {sortedMemberships.map((m) => {
            const name = m.restaurantName ?? m.slug ?? m.restaurantId;
            const isActive = m.restaurantId === activeRestaurantId;
            return (
              <button
                key={m.id}
                onClick={() => handleSwitch(m.restaurantId)}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-md px-2 py-2 text-left text-sm transition-colors",
                  isActive
                    ? "bg-accent text-accent-foreground"
                    : "hover:bg-accent/50"
                )}
              >
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-[10px] font-bold",
                    "bg-primary text-primary-foreground"
                  )}
                >
                  {getInitials(name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-xs font-medium">{name}</p>
                  {m.role && (
                    <Badge
                      variant="secondary"
                      className="mt-0.5 h-4 px-1 text-[9px] font-normal capitalize"
                    >
                      {roleLabelMap[m.role] ?? m.role}
                    </Badge>
                  )}
                </div>
                {isActive && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary" />
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
